import { useState } from "react";
import { toast } from "sonner";
import { Download, Link2, PackageCheck, ChevronDown } from "lucide-react";
import { api, fmtDate } from "../lib/api";
import { Panel } from "./ui-kit";
import { RoleBadge } from "./Badges";

export const download = async (task, a) => {
  try {
    const r = await api.get(`/tasks/${task.id}/attachments/${a.id}/download`, { responseType: "blob" });
    const url = URL.createObjectURL(r.data);
    const el = document.createElement("a");
    el.href = url;
    el.download = a.file_name;
    el.click();
    URL.revokeObjectURL(url);
  } catch (e) {
    toast.error("Download failed");
  }
};

export const AttachmentItem = ({ task, a, hideStep }) => (
  <div data-testid={`attachment-${a.id}`} className="rounded-lg border border-[#282E3D] bg-[#0B0D12] p-3">
    <div className="flex flex-wrap items-center gap-3">
      {a.file_name && <button data-testid={`attachment-download-${a.id}`} onClick={() => download(task, a)} className="inline-flex items-center gap-1.5 text-left text-sm font-medium text-slate-100 hover:text-[#FF6B00]"><Download className="h-3.5 w-3.5 shrink-0" />{a.file_name}</button>}
      {a.link && <a data-testid={`attachment-link-${a.id}`} href={a.link} target="_blank" rel="noreferrer" className="inline-flex max-w-full items-center gap-1.5 truncate text-sm text-sky-300 hover:underline"><Link2 className="h-3.5 w-3.5 shrink-0" />{a.link}</a>}
    </div>
    {a.note && <p className="mt-1 text-xs text-slate-400">{a.note}</p>}
    <div className="mt-1 font-mono text-[10px] text-slate-600">{hideStep ? "" : `Step ${a.step_index + 1} · ${task.steps[a.step_index]?.name} · `}{a.user_name} · {fmtDate(a.at)}</div>
  </div>
);

const StepGroup = ({ task, idx, outputs }) => {
  const [showOld, setShowOld] = useState(false);
  const versions = [...new Set(outputs.map((a) => a.version || 1))].sort((a, b) => b - a);
  const latest = outputs.filter((a) => (a.version || 1) === versions[0]);
  const older = outputs.filter((a) => (a.version || 1) !== versions[0]);
  const step = task.steps[idx];
  return (
    <div data-testid={`step-output-group-${idx}`} className="rounded-xl border border-emerald-500/25 bg-emerald-500/[0.03] p-4">
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <span className="font-mono text-[10px] text-emerald-400">STEP {idx + 1}</span>
        <span className="text-sm font-semibold">{step?.name}</span>
        {step && <RoleBadge role={step.role} />}
        <span className="ml-auto rounded-md border border-emerald-500/30 px-2 py-0.5 font-mono text-[10px] text-emerald-300" data-testid={`step-output-version-${idx}`}>{versions[0] > 1 ? `Latest · v${versions[0]}` : "Output"}</span>
      </div>
      <div className="space-y-2">{latest.map((a) => <AttachmentItem key={a.id} task={task} a={a} hideStep />)}</div>
      {older.length > 0 && (
        <>
          <button data-testid={`step-output-older-toggle-${idx}`} onClick={() => setShowOld(!showOld)} className="mt-2 inline-flex items-center gap-1 text-xs text-slate-500 hover:text-white">
            <ChevronDown className={`h-3 w-3 transition-transform ${showOld ? "rotate-180" : ""}`} />{showOld ? "Hide" : "Show"} earlier versions ({older.length})
          </button>
          {showOld && <div className="mt-2 space-y-2 opacity-70">{older.sort((a, b) => (b.version || 1) - (a.version || 1)).map((a) => (
            <div key={a.id}><span className="font-mono text-[10px] text-slate-500">v{a.version || 1}</span><AttachmentItem task={task} a={a} hideStep /></div>
          ))}</div>}
        </>
      )}
    </div>
  );
};

export const StepOutputs = ({ task }) => {
  const outputs = task.attachments.filter((a) => a.kind === "output");
  const steps = [...new Set(outputs.map((a) => a.step_index))].sort((a, b) => a - b);
  return (
    <Panel className="border-emerald-500/30 p-6" data-testid="step-outputs-panel">
      <div className="mb-1 flex items-center gap-2 font-heading text-lg font-semibold"><PackageCheck className="h-4 w-4 text-emerald-400" />Step outputs <span className="font-mono text-xs text-slate-500" data-testid="step-outputs-count">{outputs.length}</span></div>
      <p className="mb-4 text-xs text-slate-500">The finished file each step handed on. Download the latest one to continue the work.</p>
      {!steps.length ? <p className="text-sm text-slate-500" data-testid="step-outputs-empty">No step has handed over a file yet.</p>
        : <div className="space-y-3">{steps.map((i) => <StepGroup key={i} task={task} idx={i} outputs={outputs.filter((a) => a.step_index === i)} />)}</div>}
    </Panel>
  );
};
